import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { useModal } from 'hooks/useModal';
import { useSelector } from 'react-redux';
import { ModalsKeys, Nullable } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { useProposal } from 'modules/proposals';
import { isDesktop, isMobile } from 'utils/contentViewType';
import { Visibility, Edit, Send } from '@mui/icons-material';
import { useViewTypeContext } from 'contexts/contentViewType';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Invoice, InvoiceDetails, useInvoice } from 'modules/invoice';
import { generateQuoteProposalPageURL } from 'utils/generateQuoteProposalPageURL';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';

interface Props {
  voId: string;
  dynamicsVOId: string;
  invoiceDoc: any;
  refetchDocs: any;
  handleChangeDoc: any;
  isLoading?: boolean;
  invoice: Nullable<Invoice>;
  handleInvoiceDownload: any;
  handleOpenInvoiceDetailModal: any;
  handleCloseUploadInvoiceModal: any;
  equipmentDetails?: Nullable<ReactNode>;
  isUserHaveAccessToInvoiceAndEquipmentV2: boolean;
}

export const InvoiceTab = ({
  voId,
  invoice,
  dynamicsVOId,
  invoiceDoc,
  equipmentDetails,
  isLoading = false,
  handleInvoiceDownload,
  handleOpenInvoiceDetailModal,
  isUserHaveAccessToInvoiceAndEquipmentV2,
}: Props) => {
  const dynamicsAccountId = useSelector<any, string>(state => state.account.dynamicsAccountId);

  const { contentViewType } = useViewTypeContext();
  const { isDeleting, handleDeleteInvoiceDocument } = useInvoice();
  const { isQuoteProposalPreviewCreating, handleCreatePreviewQuoteProposal } = useProposal();

  const { handleOpen: handleOpenCreateAndSendQuoteModal } = useModal(ModalsKeys.CreateAndSendQuote);
  const { handleOpen: handleOpenUploadInvoiceDialog } = useModal(ModalsKeys.UploadInvoiceDialog);

  const classes = useStyles();

  return (
    <>
      <Box className={classes.container}>
        <Box
          className={clsx(classes.panel, {
            [classes.panelDesktop]: isDesktop(contentViewType),
            [classes.panelMobile]: isMobile(contentViewType),
          })}
        >
          <Typography component="span" variant="subtitle2">
            Details
          </Typography>

          <Stack direction="row" gap="0.5rem">
            {isUserHaveAccessToInvoiceAndEquipmentV2 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => handleOpenCreateAndSendQuoteModal({ voId })}
                >
                  Send Quote
                </Button>

                <Button
                  size="small"
                  startIcon={<Visibility />}
                  loading={isQuoteProposalPreviewCreating}
                  onClick={async () => {
                    const proposalId = await handleCreatePreviewQuoteProposal({ voId });

                    if (!proposalId) return;

                    const linkToProposalPage = generateQuoteProposalPageURL(proposalId, dynamicsAccountId);
                    window.open(linkToProposalPage, '_blank');
                  }}
                >
                  Preview
                </Button>
              </>
            )}

            <Button startIcon={<Edit />} size="small" loading={isLoading} onClick={handleOpenInvoiceDetailModal}>
              Edit details
            </Button>
          </Stack>
        </Box>

        {invoice && (
          <InvoiceDetails
            invoice={invoice}
            isLoading={isLoading}
            contentViewType={contentViewType}
            equipmentDetails={equipmentDetails}
          />
        )}

        <Box className={classes.uploadPanel}>
          <Typography component="span" variant="subtitle2">
            Upload Invoice
          </Typography>

          {!invoiceDoc ? (
            <Box className={classes.emptyDocView}>
              <Typography variant="body2" color="textSecondary">
                The invoice/quote is not uploaded yet
              </Typography>

              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => handleOpenUploadInvoiceDialog({ VOId: voId, dynamicsVOId })}
              >
                Upload Invoice/Quote
              </Button>
            </Box>
          ) : (
            <UploadedFileView
              isDeleting={isDeleting}
              type={invoiceDoc?.type}
              name={invoiceDoc?.name}
              uploadedBy={invoiceDoc?.createdBy}
              onDownload={handleInvoiceDownload}
              uploadedAt={invoiceDoc?.createdDateTime}
              onDelete={() =>
                handleDeleteInvoiceDocument(invoiceDoc.documentId, api => ({
                  refetchQueries: [api.getInvoiceDocument],
                  awaitRefetchQueries: true,
                }))
              }
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '12px',
    display: 'flex',
    marginTop: '16px',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  panel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  panelDesktop: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  panelMobile: {
    gap: '4px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  uploadPanel: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  emptyDocView: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
});
